/**
 * LessonImagePanel
 * ----------------
 * The lesson's images, for the people in the room rather than the headset.
 *
 * A lesson's diagrams and photographs have always been in the bundle and have
 * never been shown during class — the payload the players read dropped them on
 * the way through. This is the flat-screen half of putting them back: the
 * in-world panel draws the same picture for anyone in VR, and both follow the
 * same selection.
 *
 * The teacher picks; everyone else follows. A student gets no say in WHICH
 * image the class is on, because a class looking at four different pictures is
 * not a class. How big it is on their own screen is theirs, though: collapsing
 * the card or enlarging it changes nothing for anyone else.
 */

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Images, Maximize2, X } from 'lucide-react';
import type { ActiveLessonImage } from '../../lib/lesson/buildActiveLesson';

interface LessonImageGridProps {
  images: ActiveLessonImage[];
  /** Id of the image the class is on, or null when none is being shown. */
  activeImageId: string | null;
  onSelect: (imageId: string | null) => void;
}

/**
 * The teacher's picker, for the dock's Images popover.
 *
 * It replaces a strip of thumbnails that sat in the middle of the stage — over
 * the very models the image was meant to go with — and scrolled sideways with
 * no names, so a lesson with a dozen diagrams was a guessing game. Three named
 * columns scroll down instead. Picking the image that is already up takes it
 * down again.
 */
export const LessonImageGrid = ({ images, activeImageId, onSelect }: LessonImageGridProps) => (
  <div className="grid max-h-72 w-[min(22rem,calc(100vw-2.5rem))] grid-cols-3 gap-1.5 overflow-y-auto overscroll-contain">
    {images.map((image) => {
      const isActive = image.id === activeImageId;
      return (
        <button
          key={image.id}
          type="button"
          onClick={() => onSelect(isActive ? null : image.id)}
          aria-pressed={isActive}
          title={image.name || 'Lesson image'}
          className={`flex min-w-0 flex-col overflow-hidden rounded-lg border text-left transition ${
            isActive
              ? 'border-cyan-300/70 bg-cyan-400/10 ring-2 ring-cyan-300/30'
              : 'border-white/12 bg-white/[0.04] hover:border-white/30'
          }`}
        >
          <img
            src={image.thumbnail_url || image.url}
            alt=""
            loading="lazy"
            className="aspect-[4/3] w-full object-cover"
          />
          <span className={`truncate px-1.5 py-1 text-[10px] font-medium ${isActive ? 'text-cyan-50' : 'text-white/75'}`}>
            {image.name || 'Lesson image'}
          </span>
        </button>
      );
    })}
  </div>
);

/**
 * Whether THIS viewer keeps the card collapsed. Remembered in their own
 * browser, never synced: it is a preference about their screen, not a choice
 * the class follows.
 */
const COLLAPSED_KEY = 'learnxr_lesson_image_card:collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean) {
  try {
    if (collapsed) localStorage.setItem(COLLAPSED_KEY, '1');
    else localStorage.removeItem(COLLAPSED_KEY);
  } catch {
    /* storage blocked (private mode): the choice lasts for this visit only */
  }
}

/** True while the user is typing somewhere, where the arrow keys and Escape are theirs. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName);
}

interface LessonImageViewerProps {
  images: ActiveLessonImage[];
  activeImageId: string | null;
  /** Host-only; students are handed nothing to call. */
  onSelect?: (imageId: string | null) => void;
  isHost: boolean;
  /**
   * Arrow keys step the image only while the pointer is over the card (or it
   * is enlarged). For players whose scene already owns the arrow keys: krpano
   * walks or pans with them, and a host looking around must not flip the
   * class's image with every press.
   */
  arrowKeysNeedPointer?: boolean;
  /**
   * Which edge the card docks to. Right by default; the player moves it left
   * when the lesson layout puts the 3D panel on the right, so the card never
   * covers the panel.
   */
  side?: 'left' | 'right';
}

/**
 * The image itself, as a card docked on the right, for the chrome's overlay
 * slot. Everyone sees it.
 *
 * It used to cover the whole screen behind a dark backdrop. That stopped the
 * lesson it was meant to support: the model the diagram explained was hidden,
 * and nobody could reach the lesson panel until the teacher closed it. Docked
 * to the side with no backdrop, the scene stays visible and usable, and the
 * full-screen view is still one press away as a local "Enlarge".
 */
export const LessonImageViewer = ({ images, activeImageId, onSelect, isHost, arrowKeysNeedPointer = false, side = 'right' }: LessonImageViewerProps) => {
  const activeIndex = images.findIndex((image) => image.id === activeImageId);
  const active = activeIndex >= 0 ? images[activeIndex] : null;

  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [enlarged, setEnlarged] = useState(false);
  // A ref, not state: it is read only inside the key handler, and hovering
  // in and out of the card should not re-render it.
  const pointerOnCardRef = useRef(false);

  // An enlarged view belongs to the image being shown. Without this, the next
  // image the teacher put up would open straight into a full-screen view the
  // viewer had long since forgotten asking for.
  const showing = !!active;
  useEffect(() => {
    if (!showing) setEnlarged(false);
    // The card unmounts under the pointer without a pointerleave.
    if (!showing) pointerOnCardRef.current = false;
  }, [showing]);

  /*
    Arrow keys and Escape, for a teacher presenting with a keyboard to hand.

    The card no longer blocks the page, so the teacher can be typing elsewhere
    while an image is up — arrows in a text field must move the caret, not the
    class — or have a popover or dialog open, whose own Escape handler marks the
    key as used. Escape closes this viewer's enlarged view first, and only then
    (for the host) takes the image down for everyone.
  */
  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || isTypingTarget(event.target)) return;
      if (event.key === 'Escape') {
        if (enlarged) setEnlarged(false);
        else if (isHost) onSelect?.(null);
        return;
      }
      if (!isHost || images.length < 2) return;
      if (arrowKeysNeedPointer && !enlarged && !pointerOnCardRef.current) return;
      if (event.key === 'ArrowRight') {
        onSelect?.(images[(activeIndex + 1) % images.length].id);
      }
      if (event.key === 'ArrowLeft') {
        onSelect?.(images[(activeIndex - 1 + images.length) % images.length].id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isHost, active, activeIndex, images, onSelect, enlarged, arrowKeysNeedPointer]);

  if (!active) return null;

  const name = active.name || 'Lesson image';

  const step = (delta: number) => {
    if (images.length === 0) return;
    const from = activeIndex >= 0 ? activeIndex : 0;
    const next = (from + delta + images.length) % images.length;
    onSelect?.(images[next].id);
  };

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    writeCollapsed(next);
  };

  const iconButton =
    'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/65 transition hover:bg-white/10 hover:text-white';
  const stepButton =
    'absolute top-1/2 inline-flex -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-zinc-950/70 text-white/80 backdrop-blur transition hover:bg-zinc-900 hover:text-white';

  return (
    <>
      <section
        aria-label={`Lesson image: ${name}`}
        onPointerEnter={() => { pointerOnCardRef.current = true; }}
        onPointerLeave={() => { pointerOnCardRef.current = false; }}
        className={`pointer-events-auto absolute ${side === 'left' ? 'left-3' : 'right-3'} top-[calc(var(--hud-top)+0.5rem)] z-stage flex max-h-[55%] w-[min(24rem,max(34vw,12rem))] flex-col overflow-hidden rounded-2xl border border-white/12 bg-zinc-950/85 shadow-2xl backdrop-blur-2xl`}
      >
        <header className="flex shrink-0 items-center gap-1 py-1.5 pl-2.5 pr-1.5">
          <Images className="h-3.5 w-3.5 shrink-0 text-cyan-300/80" />
          <p className="ml-0.5 min-w-0 flex-1 truncate text-xs font-semibold text-white" title={name}>
            {name}
          </p>
          {images.length > 1 && (
            <span className="shrink-0 px-1 text-[11px] tabular-nums text-white/50">
              {activeIndex + 1}/{images.length}
            </span>
          )}
          <button
            type="button"
            onClick={() => setEnlarged(true)}
            aria-label="Enlarge image"
            title="Enlarge (only on your screen)"
            className={iconButton}
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand image' : 'Collapse image'}
            title={collapsed ? 'Show the image (only on your screen)' : 'Collapse to the title (only on your screen)'}
            className={iconButton}
          >
            {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </button>
          {isHost && (
            <button
              type="button"
              onClick={() => onSelect?.(null)}
              aria-label="Close image"
              title="Stop showing this image"
              className={iconButton}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </header>

        {!collapsed && (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2.5 pb-2.5">
            <div className="relative">
              <button
                type="button"
                onClick={() => setEnlarged(true)}
                aria-label="Enlarge image"
                className="block w-full cursor-zoom-in"
              >
                <img
                  src={active.url}
                  alt={name}
                  className="max-h-[38vh] w-full rounded-lg border border-white/10 bg-black/30 object-contain"
                />
              </button>
              {isHost && images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => step(-1)}
                    aria-label="Previous image"
                    className={`${stepButton} left-1 h-8 w-8`}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => step(1)}
                    aria-label="Next image"
                    className={`${stepButton} right-1 h-8 w-8`}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
            {active.description && (
              <p className="mt-1.5 line-clamp-3 text-[11px] leading-snug text-white/60">{active.description}</p>
            )}
          </div>
        )}
      </section>

      {/* Enlarge: this viewer's own full-screen look, dismissed by them alone. */}
      {enlarged && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={name}
          onClick={() => setEnlarged(false)}
          className="pointer-events-auto fixed inset-0 z-drawer flex flex-col items-center justify-center bg-black/85 p-4 backdrop-blur-sm sm:p-8"
        >
          <div className="flex max-h-full w-full max-w-5xl flex-col" onClick={(event) => event.stopPropagation()}>
            <div className="mb-2 flex items-center gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-white">{name}</p>
                {active.description && <p className="truncate text-xs text-white/60">{active.description}</p>}
              </div>
              {images.length > 1 && (
                <span className="ml-auto shrink-0 rounded-full border border-white/12 bg-white/[0.06] px-2.5 py-1 text-xs font-semibold text-white/70">
                  {activeIndex + 1} / {images.length}
                </span>
              )}
              <button
                type="button"
                onClick={() => setEnlarged(false)}
                aria-label="Close enlarged view"
                title="Back to the lesson (the image stays up)"
                className={`${images.length > 1 ? '' : 'ml-auto '}inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/12 bg-white/[0.06] text-white/70 transition hover:bg-white/12 hover:text-white`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="relative flex min-h-0 flex-1 items-center justify-center">
              <img
                src={active.url}
                alt={name}
                className="max-h-[75vh] w-auto max-w-full rounded-xl border border-white/10 object-contain shadow-2xl"
              />
              {isHost && images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => step(-1)}
                    aria-label="Previous image"
                    className={`${stepButton} left-0 h-11 w-11`}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => step(1)}
                    aria-label="Next image"
                    className={`${stepButton} right-0 h-11 w-11`}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
